import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { UserCog, CheckCircle2, XCircle, ExternalLink, FileText, Ban, Award, Plus, Loader2, Pencil, Trash2 } from "lucide-react";

/**
 * Painel admin de profissionais credenciados. Aprova/reprova/bloqueia,
 * concede selo (Ouro/Prata/Bronze) e cria profissionais manualmente.
 */

const STATUS_CHIP = {
  draft:    "bg-slate-100 text-slate-700",
  pending:  "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
};
const STATUS_LABEL = { draft: "Rascunho", pending: "Em análise", approved: "Aprovado", rejected: "Reprovado" };

const TIER_CHIP = {
  ouro:   "bg-gradient-to-r from-[#F1C232] to-[#D4A017] text-[#3B2F0B]",
  prata:  "bg-gradient-to-r from-slate-300 to-slate-500 text-slate-900",
  bronze: "bg-gradient-to-r from-[#CD7F32] to-[#8B4513] text-white",
};
const PAY_CHIP = {
  paid:      "bg-emerald-100 text-emerald-800",
  waived:    "bg-blue-100 text-blue-800",
  pending:   "bg-amber-100 text-amber-800",
  cancelled: "bg-slate-100 text-slate-500",
};

const ProfessionalsTable = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [sealing, setSealing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    const url = filter === "all" ? "/admin/professionals" : `/admin/professionals?status=${filter}`;
    api.get(url).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(load, [filter]);   // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (id, action, reason) => {
    try {
      const url = `/admin/professionals/${id}/${action}`;
      const body = action === "reject" ? { reason } : {};
      await api.post(url, body);
      toast.success(`Ação executada: ${action}`);
      load(); setSelected(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const del = async (p) => {
    if (!window.confirm(
      `Excluir ${p.display_name || p.name}?\n\n` +
      `ATENÇÃO: Esta ação é IRREVERSÍVEL. O registro será apagado e o admin\n` +
      `NÃO consegue reverter depois. O e-mail ficará livre — a pessoa pode\n` +
      `se cadastrar novamente, mas será uma conta nova SEM HISTÓRICO.\n\n` +
      `Prefira BLOQUEAR (banimento reversível) em vez de excluir sempre que possível.\n\n` +
      `Se o profissional tiver consultas no histórico, a exclusão será\n` +
      `bloqueada automaticamente.`,
    )) return;
    try {
      await api.delete(`/admin/professionals/${p.id}`);
      toast.success("Profissional excluído");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const isCertActive = (p) => {
    if (!p.certification_tier) return false;
    if (!["paid", "waived"].includes((p.certification_payment_status || "").toLowerCase())) return false;
    if (!p.certification_expires_at) return false;
    return new Date(p.certification_expires_at) > new Date();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="admin-professionals-panel">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <UserCog className="w-5 h-5 text-slate-500" />
          <h2 className="font-display font-bold text-lg text-[#0B2B62]">Profissionais credenciados ({items.length})</h2>
        </div>
        <div className="flex gap-1 items-center">
          {["pending", "approved", "rejected", "draft", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              data-testid={`prof-filter-${f}`}
              className={`text-xs px-2.5 py-1.5 rounded-md ${filter === f ? "bg-[#0B2B62] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
              {STATUS_LABEL[f] || "Todos"}
            </button>
          ))}
          <button onClick={() => setCreating(true)} data-testid="prof-create-btn"
            className="ml-2 text-xs px-3 py-1.5 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white font-bold inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Novo Profissional
          </button>
        </div>
      </div>
      <div className="lg:hidden px-4 pt-2 text-[11px] text-slate-400 italic">← Arraste a tabela para o lado para ver todas as colunas →</div>
      <div className="overflow-x-auto relative" style={{ WebkitOverflowScrolling: "touch" }}>
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-3 py-2.5 text-left">Foto</th>
              <th className="px-3 py-2.5 text-left">Nome</th>
              <th className="px-3 py-2.5 text-left">Conselho</th>
              <th className="px-3 py-2.5 text-left">Categorias</th>
              <th className="px-3 py-2.5 text-left">Valor</th>
              <th className="px-3 py-2.5 text-left">Status</th>
              <th className="px-3 py-2.5 text-left">Selo</th>
              <th className="px-3 py-2.5 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-sm text-slate-400">Nenhum profissional neste filtro.</td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50" data-testid={`admin-professional-${p.id}`}>
                <td className="px-3 py-2.5">
                  {p.photo_url ? <img src={p.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-slate-100" />}
                </td>
                <td className="px-3 py-2.5">
                  <div className="font-semibold text-slate-800">{p.display_name || p.name}</div>
                  <div className="text-[11px] text-slate-500">{p.email}</div>
                </td>
                <td className="px-3 py-2.5 text-xs">{p.council_type} {p.council_number}/{p.council_state}</td>
                <td className="px-3 py-2.5 text-[11px] text-slate-600 max-w-[180px] truncate">{(p.categories || []).join(", ") || "—"}</td>
                <td className="px-3 py-2.5 text-xs tabular-nums">R$ {Number(p.session_value || 0).toFixed(2)}</td>
                <td className="px-3 py-2.5">
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_CHIP[p.status] || STATUS_CHIP.draft}`}>
                    {STATUS_LABEL[p.status] || "Rascunho"}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  {p.certification_tier ? (
                    <div className="flex flex-col gap-0.5">
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest ${TIER_CHIP[p.certification_tier] || ""} ${isCertActive(p) ? "" : "opacity-50"}`}>
                        <Award className="w-2.5 h-2.5 inline mr-0.5" />
                        {p.certification_tier}
                      </span>
                      {p.certification_is_lifetime ? (
                        <span className="text-[9px] px-1 py-0.5 rounded uppercase font-bold bg-amber-100 text-amber-900 border border-amber-300"
                          title="Selo vitalício — não expira">
                          ★ vitalício
                        </span>
                      ) : (
                        <span className={`text-[9px] px-1 py-0.5 rounded uppercase font-bold ${PAY_CHIP[p.certification_payment_status] || PAY_CHIP.pending}`}>
                          {p.certification_payment_status || "pending"}
                        </span>
                      )}
                    </div>
                  ) : <span className="text-[10px] text-slate-400">sem selo</span>}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => setEditing(p)} data-testid={`prof-edit-${p.id}`}
                      title="Editar dados cadastrais"
                      className="text-blue-600 hover:bg-blue-600 hover:text-white p-1.5 rounded-md border border-blue-500/30 transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setSealing(p)} data-testid={`prof-seal-${p.id}`}
                      title="Conceder/revogar selo Ouro/Prata/Bronze"
                      className="text-amber-600 hover:bg-amber-500 hover:text-white p-1.5 rounded-md border border-amber-500/30 transition-colors">
                      <Award className="w-4 h-4" />
                    </button>
                    <button onClick={() => setSelected(p)} data-testid={`prof-view-${p.id}`}
                      className="text-[#0B2B62] hover:bg-[#0B2B62] hover:text-white p-1.5 rounded-md border border-[#0B2B62]/20 transition-colors">
                      <FileText className="w-4 h-4" />
                    </button>
                    {p.status === "pending" && (
                      <>
                        <button onClick={() => act(p.id, "approve")} data-testid={`prof-approve-${p.id}`}
                          className="text-emerald-700 hover:bg-emerald-700 hover:text-white p-1.5 rounded-md border border-emerald-500/20 transition-colors" title="Aprovar">
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => {
                          const r = window.prompt("Motivo da reprovação:");
                          if (r) act(p.id, "reject", r);
                        }} data-testid={`prof-reject-${p.id}`}
                          className="text-red-500 hover:bg-red-500 hover:text-white p-1.5 rounded-md border border-red-400/20 transition-colors" title="Reprovar">
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button onClick={() => act(p.id, p.blocked ? "unblock" : "block")}
                      data-testid={`prof-block-${p.id}`}
                      className={`p-1.5 rounded-md border transition-colors ${p.blocked ? "text-slate-400 border-slate-200" : "text-slate-500 hover:bg-red-50 hover:text-red-600 border-slate-200"}`}
                      title={p.blocked ? "Desbloquear" : "Bloquear login"}>
                      <Ban className="w-4 h-4" />
                    </button>
                    <button onClick={() => del(p)} data-testid={`prof-delete-${p.id}`}
                      title="Excluir profissional"
                      className="text-red-600 hover:bg-red-600 hover:text-white p-1.5 rounded-md border border-red-400/30 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <ProfessionalDetailModal p={selected} onClose={() => setSelected(null)} onAct={act} />}
      {sealing && <SealModal
        target={sealing}
        endpoint={`/admin/professionals/${sealing.id}/certification`}
        onClose={() => setSealing(null)}
        onSaved={() => { setSealing(null); load(); }}
      />}
      {creating && <CreateProfessionalModal
        onClose={() => setCreating(false)}
        onSaved={() => { setCreating(false); setFilter("approved"); load(); }}
      />}
      {editing && <EditProfessionalModal
        p={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />}
    </div>
  );
};

const ProfessionalDetailModal = ({ p, onClose, onAct }) => (
  <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="prof-detail-modal">
    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 space-y-4">
      <div className="flex items-start gap-4">
        {p.photo_url && <img src={p.photo_url} alt="" className="w-20 h-20 rounded-full object-cover" />}
        <div>
          <h3 className="font-display font-bold text-xl text-[#0B2B62]">{p.display_name || p.name}</h3>
          <div className="text-xs text-slate-500">{p.email} · {p.phone}</div>
          <div className="text-xs text-slate-500 mt-1">{p.council_type} {p.council_number}/{p.council_state}</div>
        </div>
      </div>
      {p.bio && <div><div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Bio</div><div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{p.bio}</div></div>}
      {p.curriculum_file_url && <a href={p.curriculum_file_url} target="_blank" rel="noreferrer" className="text-sm text-[#0B2B62] underline inline-flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> Currículo em PDF</a>}
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Documentos ({(p.documents || []).length})</div>
        <div className="mt-1 flex flex-wrap gap-2">
          {(p.documents || []).map((url) => <a key={url} href={url} target="_blank" rel="noreferrer" className="text-xs text-[#0B2B62] underline break-all">{url.split("/").pop()}</a>)}
          {(p.documents || []).length === 0 && <span className="text-xs text-slate-400">Nenhum</span>}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-xs pt-2 border-t border-slate-100">
        <div><b>Duração</b><br />{p.session_duration} min</div>
        <div><b>Valor</b><br />R$ {Number(p.session_value || 0).toFixed(2)}</div>
        <div><b>Agenda</b><br />{(p.availability || []).length} janela(s)</div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onClose} className="text-sm text-slate-600 px-3 py-2">Fechar</button>
        {p.status === "pending" && (
          <>
            <button onClick={() => { const r = window.prompt("Motivo da reprovação:"); if (r) onAct(p.id, "reject", r); }} className="text-sm text-white bg-red-500 hover:bg-red-600 px-4 py-2 rounded-md">Reprovar</button>
            <button onClick={() => onAct(p.id, "approve")} className="text-sm text-white bg-emerald-700 hover:bg-emerald-800 px-4 py-2 rounded-md">Aprovar</button>
          </>
        )}
      </div>
    </div>
  </div>
);

export const SealModal = ({ target, endpoint, onClose, onSaved, kind = "profissional" }) => {
  // v0.18 — Sanitiza payment_status inicial: se vier "trial" (do backend antigo)
  // ou qualquer valor não-canônico, cai pra "paid". Backend só aceita 4 valores.
  const VALID_PAYMENT = ["paid", "pending", "waived", "cancelled"];
  const initialPayment = VALID_PAYMENT.includes(target?.certification_payment_status)
    ? target.certification_payment_status
    : "paid";
  const [tier, setTier] = useState(target?.certification_tier || "");
  const [months, setMonths] = useState(1);
  const [payment, setPayment] = useState(initialPayment);
  const [note, setNote] = useState("");
  const [isLifetime, setIsLifetime] = useState(!!target?.certification_is_lifetime);
  const [isTrial, setIsTrial] = useState(!!target?.certification_is_trial);
  const [saving, setSaving] = useState(false);

  // Trial e Vitalício são mutuamente exclusivos. Ao marcar vitalício, garante
  // que o payment vá pra "waived" (cortesia), que é o default esperado.
  const setLifetime = (v) => {
    setIsLifetime(v);
    if (v) { setIsTrial(false); setPayment("waived"); }
  };
  const setTrial = (v) => { setIsTrial(v); if (v) { setIsLifetime(false); setMonths(3); } };

  const prices = kind === "clinica"
    ? { ouro: 300, prata: 200, bronze: 100 }
    : { ouro: 150, prata: 100, bronze: 50 };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(endpoint, {
        tier, months: Number(months), payment_status: payment, note,
        is_lifetime: isLifetime, is_trial: isTrial,
      });
      toast.success(tier
        ? (isLifetime ? `Selo ${tier.toUpperCase()} VITALÍCIO concedido`
          : isTrial ? `Trial ${tier.toUpperCase()} de ${months} meses concedido`
          : `Selo ${tier.toUpperCase()} concedido`)
        : "Selo revogado");
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="seal-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
        <div>
          <h3 className="font-display font-bold text-xl text-[#0B2B62] inline-flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-600" /> Selar {kind}
          </h3>
          <p className="text-xs text-slate-500 mt-1">{target?.display_name || target?.name || target?.code}</p>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Tier</label>
          <div className="mt-2 grid grid-cols-4 gap-2">
            {[["", "Nenhum"], ["bronze", `Bronze · R$${prices.bronze}`], ["prata", `Prata · R$${prices.prata}`], ["ouro", `Ouro · R$${prices.ouro}`]].map(([v, l]) => (
              <button key={v} onClick={() => setTier(v)} data-testid={`seal-tier-${v || "none"}`}
                className={`text-[11px] px-2 py-2 rounded-md border font-semibold ${
                  tier === v ? (v === "ouro" ? "bg-amber-400 text-slate-900 border-amber-500" : v === "prata" ? "bg-slate-300 text-slate-900 border-slate-500" : v === "bronze" ? "bg-orange-400 text-white border-orange-600" : "bg-slate-800 text-white border-slate-900") : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                }`}>{l}</button>
            ))}
          </div>
        </div>

        {tier && (
          <>
            <label className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-md cursor-pointer hover:bg-amber-100 transition-colors"
              data-testid="seal-lifetime-toggle">
              <input type="checkbox" checked={isLifetime} onChange={(e) => setLifetime(e.target.checked)}
                className="mt-0.5 accent-amber-600" />
              <div className="flex-1">
                <div className="text-xs font-bold text-amber-900 uppercase tracking-wide inline-flex items-center gap-1">
                  <Award className="w-3.5 h-3.5" /> Selo VITALÍCIO (por merecimento)
                </div>
                <div className="text-[10px] text-amber-800 mt-0.5">
                  Sem cobrança e sem prazo — reservado para parceiros históricos ou casos especiais. Não expira.
                </div>
              </div>
            </label>
            {!isLifetime && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Validade (meses)</label>
                  <input type="number" min={1} max={24} value={months} onChange={(e) => setMonths(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="seal-months" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Pagamento</label>
                  <select value={payment} onChange={(e) => setPayment(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white" data-testid="seal-payment">
                    <option value="paid">Pago</option>
                    <option value="pending">Pendente</option>
                    <option value="waived">Cortesia/Parceria</option>
                    <option value="cancelled">Cancelado</option>
                  </select>
                </div>
              </div>
            )}
            {isLifetime && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Pagamento</label>
                <select value={payment} onChange={(e) => setPayment(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white" data-testid="seal-payment">
                  <option value="waived">Cortesia/Parceria (vitalício)</option>
                  <option value="paid">Pago</option>
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Observação (auditoria)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Ex.: cortesia RAF, teste MVP, parceria X…"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="seal-note" />
            </div>
          </>
        )}
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100 text-[11px] text-slate-500">
          <span>
            {tier
              ? <>Só aparece na vitrine se pagamento for <b>pago</b> ou <b>cortesia</b>.</>
              : "Revoga o selo — profissional some da vitrine pública."}
          </span>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-slate-600 px-3 py-2">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="seal-save"
            className="text-sm text-white bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 px-4 py-2 rounded-md inline-flex items-center gap-1.5">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {tier ? "Salvar selo" : "Revogar"}
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateProfessionalModal = ({ onClose, onSaved }) => {
  const [f, setF] = useState({
    name: "", email: "", phone: "", display_name: "",
    categories: "", council_type: "CRM", council_number: "", council_state: "SP",
    bio: "", session_duration: 50, session_value: 250,
  });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.name || !f.email) { toast.error("Nome e e-mail são obrigatórios"); return; }
    setSaving(true);
    try {
      const body = {
        ...f,
        categories: f.categories.split(",").map((s) => s.trim()).filter(Boolean),
        session_duration: Number(f.session_duration) || 50,
        session_value: Number(f.session_value) || 0,
      };
      const r = await api.post("/admin/professionals", body);
      setCreated(r.data);
      toast.success("Profissional criado — copie a senha temporária abaixo");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao criar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="prof-create-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <div>
          <h3 className="font-display font-bold text-xl text-[#0B2B62] inline-flex items-center gap-2">
            <Plus className="w-5 h-5 text-emerald-700" /> Novo Profissional
          </h3>
          <p className="text-xs text-slate-500 mt-1">Nasce <b>aprovado</b> — pronto pra selar e ir pra vitrine. Senha temporária é gerada; o profissional troca no primeiro login.</p>
        </div>

        {created ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 space-y-2">
            <div className="text-sm font-bold text-emerald-800">Profissional criado com sucesso!</div>
            <div className="text-xs text-slate-700"><b>E-mail:</b> {created.professional.email}</div>
            <div className="text-xs text-slate-700"><b>ID:</b> {created.professional.id}</div>
            <div className="p-2 bg-white border border-emerald-300 rounded text-xs font-mono">
              <b>Senha temporária:</b> {created.temp_password}
            </div>
            <p className="text-[11px] text-slate-500">Copie e envie ao profissional (WhatsApp/e-mail seguro). Ele deve trocar no primeiro login.</p>
            <button onClick={onSaved} data-testid="prof-create-done"
              className="w-full mt-2 px-4 py-2 rounded-md bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-800">
              Fechar e recarregar lista
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome completo *"><input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="cp-name"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
              <Field label="Nome de exibição"><input value={f.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid="cp-display-name" placeholder="Ex.: Dr. João da Silva"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
              <Field label="E-mail *"><input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="cp-email"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
              <Field label="Telefone"><input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="cp-phone" placeholder="(11) 9…"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
              <Field label="Conselho"><select value={f.council_type} onChange={(e) => set("council_type", e.target.value)} data-testid="cp-council-type"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                {["CRM","CRP","CFN","CRN","CRT","CBO","COREN","CREF","CRESS","CREFITO","CRF","CREFONO","OUTRO"].map((v) => <option key={v} value={v}>{v}</option>)}
              </select></Field>
              <Field label="Nº conselho / UF">
                <div className="flex gap-2">
                  <input value={f.council_number} onChange={(e) => set("council_number", e.target.value)} data-testid="cp-council-num" placeholder="123456"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
                  <input value={f.council_state} onChange={(e) => set("council_state", e.target.value.toUpperCase().slice(0,2))} data-testid="cp-council-state" placeholder="SP"
                    className="w-16 px-3 py-2 border border-slate-300 rounded-md text-sm" />
                </div>
              </Field>
              <Field label="Categorias (separadas por vírgula)"><input value={f.categories} onChange={(e) => set("categories", e.target.value)} data-testid="cp-categories"
                placeholder="Psiquiatra, Psicólogo(a)"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Duração (min)"><input type="number" value={f.session_duration} onChange={(e) => set("session_duration", e.target.value)} data-testid="cp-duration"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
                <Field label="Valor (R$)"><input type="number" step="0.01" value={f.session_value} onChange={(e) => set("session_value", e.target.value)} data-testid="cp-value"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
              </div>
            </div>
            <Field label="Bio"><textarea rows={3} value={f.bio} onChange={(e) => set("bio", e.target.value)} data-testid="cp-bio"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="text-sm text-slate-600 px-3 py-2">Cancelar</button>
              <button onClick={save} disabled={saving} data-testid="cp-save"
                className="text-sm text-white bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 px-4 py-2 rounded-md inline-flex items-center gap-1.5">
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Criar profissional
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const EditProfessionalModal = ({ p, onClose, onSaved }) => {
  const [f, setF] = useState({
    name: p.name || "",
    display_name: p.display_name || "",
    phone: p.phone || "",
    council_type: p.council_type || "CRM",
    council_number: p.council_number || "",
    council_state: p.council_state || "SP",
    categories: (p.categories || []).join(", "),
    bio: p.bio || "",
    session_duration: p.session_duration || 50,
    session_value: p.session_value || 0,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Nome é obrigatório"); return; }
    setSaving(true);
    try {
      const body = {
        ...f,
        categories: f.categories.split(",").map((s) => s.trim()).filter(Boolean),
        session_duration: Number(f.session_duration) || 50,
        session_value: Number(f.session_value) || 0,
      };
      await api.patch(`/admin/professionals/${p.id}`, body);
      toast.success("Profissional atualizado");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="prof-edit-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <div>
          <h3 className="font-display font-bold text-xl text-[#0B2B62] inline-flex items-center gap-2">
            <Pencil className="w-5 h-5 text-blue-600" /> Editar profissional
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            E-mail (<b>{p.email}</b>) não pode ser alterado — ele é usado como login.
            Para trocar, exclua e crie novamente.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Nome completo *"><input value={f.name} onChange={(e) => set("name", e.target.value)}
            data-testid="ep-name" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
          <Field label="Nome de exibição"><input value={f.display_name} onChange={(e) => set("display_name", e.target.value)}
            data-testid="ep-display-name" placeholder="Dr. João da Silva"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
          <Field label="Telefone"><input value={f.phone} onChange={(e) => set("phone", e.target.value)}
            data-testid="ep-phone" placeholder="(11) 9…"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
          <Field label="Conselho"><select value={f.council_type} onChange={(e) => set("council_type", e.target.value)}
            data-testid="ep-council-type"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
            {["CRM","CRP","CRN","CREFITO","CREFONO","COFEN","CREF","CRESS","OUTRO"].map((v) => <option key={v} value={v}>{v}</option>)}
          </select></Field>
          <Field label="Nº conselho / UF">
            <div className="flex gap-2">
              <input value={f.council_number} onChange={(e) => set("council_number", e.target.value)}
                data-testid="ep-council-num" placeholder="123456"
                className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
              <input value={f.council_state} onChange={(e) => set("council_state", e.target.value.toUpperCase().slice(0, 2))}
                data-testid="ep-council-state" placeholder="SP"
                className="w-16 px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </div>
          </Field>
          <Field label="Categorias (separadas por vírgula)"><input value={f.categories} onChange={(e) => set("categories", e.target.value)}
            data-testid="ep-categories" placeholder="Psiquiatra, Psicólogo(a)"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
          <Field label="Duração (min)"><input type="number" value={f.session_duration}
            onChange={(e) => set("session_duration", e.target.value)}
            data-testid="ep-duration"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
          <Field label="Valor (R$)"><input type="number" step="0.01" value={f.session_value}
            onChange={(e) => set("session_value", e.target.value)}
            data-testid="ep-value"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
        </div>
        <Field label="Bio"><textarea rows={4} value={f.bio} onChange={(e) => set("bio", e.target.value)}
          data-testid="ep-bio" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={onClose} className="text-sm text-slate-600 px-3 py-2">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="ep-save"
            className="text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-4 py-2 rounded-md inline-flex items-center gap-1.5">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Salvar alterações
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalsTable;
